import React, { useEffect, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import size from 'lodash/size';
import get from 'lodash/get';
import { useSelector, useDispatch } from 'react-redux';

import Button from '../../Button';
import TextInput from '../../TextInput';
import Loader from '../../common/Loader';
import Checkbox from '../../common/Checkbox';
import SelectCustom from '../../SelectCustom';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import AppointmentHeader from '../Components/AppointmentHeader';

import { IDS } from '../../../constants/ids';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import {
  INLINE_TYPES,
  FORBIDDEN_VISIT_TYPES_FOR_MAP,
} from '../../../constants';

import { enhance } from './enhance';
import { usePageView } from '../../../hocs/usePageView';
import { useFormState } from '../../../hocs/useFormWithValidation';
import useNavigateToPreviousPage from '../hooks/useNavigateToPreviousPage';
import useAppointmentVisitDetailsStateData from './hooks/useAppointmentVisitDetailsStateData';

import { selectIsEirTelemedicineVisitTypeCode } from '../../../redux/appointmentForm/selectors';

import {
  isNonEmptyString,
  isNonEmptyArrayOrString,
} from './helpers/checkFieldValues';
import {
  FIELD_FILLING_TYPES,
  getFieldProps,
} from '../../../utils/fields/getFieldProps';
import { appointmentProgressActions } from '../../../redux/appointmentForm/progress-types';
import { redirectToRescheduleProviderDetails } from '../../../utils/appointments/redirect-utils';

const { OTHER_REASON_TYPE, SUBMIT_REASON_TYPE } = IDS.APPOINTMENT_FORM;
const {
  REASON_NOTE,
  REASON_TYPE,
  REASON_VISIT,
  COUNTRY,
  STATE,
  IS_HOME_LOCATION_DIFFERENT,
} = PROFILE_FIELDS_TYPES;

function AppointmentVisitDetails({
  formatMessage,
  loading,
  fetchReasonVisit,
  setReason,
  savedReason,
  reasonTypesList,
  reasonVisitList,
  isUrgentVisitFlow,
  visitTypeCode,
  setVisitType,
  allVisitTypes,
  isRescheduleFlow,
  rescheduleData,
  countries,
  organization,
}) {
  usePageView('Get Care | Visit Details');

  const dispatch = useDispatch();

  const isEirTelemedicineVisitType = useSelector(
    selectIsEirTelemedicineVisitTypeCode,
  );

  const isInlineVisitType = INLINE_TYPES.includes(visitTypeCode);

  const isVirtualVisitType = FORBIDDEN_VISIT_TYPES_FOR_MAP.includes(
    visitTypeCode,
  );

  const {
    initCountryFormValue,
    stateList,
    stateName: careUserStateName,
    stateOptions,
  } = useAppointmentVisitDetailsStateData();

  const { navigateToPrevPage } = useNavigateToPreviousPage();

  const initialFormValues = useMemo(() => {
    const values = { ...savedReason };

    const reasonType = get(savedReason, 'reasonType.id', '');

    if (reasonType) {
      values[REASON_TYPE] = reasonType;
    }

    return values;
  }, [savedReason]);

  const { formState, handleFormChange } = useFormState(initialFormValues);

  const makeField = getFieldProps(formatMessage, handleFormChange);

  const {
    reasonType,
    reasonNote,
    reasonVisit = [],
    isHomeLocationDifferent,
    state,
    country,
  } = formState;

  const hasReasonNote = useMemo(
    () => reasonVisit.some(({ value }) => value === OTHER_REASON_TYPE),
    [reasonVisit],
  );

  const reasonVisitListOptions = useMemo(
    () =>
      reasonVisitList.concat({
        value: OTHER_REASON_TYPE,
        label: formatMessage({ id: 'common.other' }),
      }),
    [reasonVisitList],
  );

  const isLimitedByStateCertification = get(
    organization,
    'limitedByStateCertification',
    false,
  );

  const showHomeLocationCheckbox =
    isLimitedByStateCertification &&
    (isVirtualVisitType || isEirTelemedicineVisitType);

  const isOnlyOneReasonTypeInList = !loading && size(reasonTypesList) === 1;

  const isValid = useMemo(() => {
    const getBaseValidation = () => {
      if (!reasonType) return false;

      if (isUrgentVisitFlow) {
        if (hasReasonNote) {
          return isNonEmptyString(reasonNote);
        }
        return isNonEmptyArrayOrString(reasonVisit);
      }

      return isNonEmptyString(reasonNote);
    };

    const baseValidation = getBaseValidation();

    return isHomeLocationDifferent
      ? baseValidation && country && state
      : baseValidation;
  }, [
    isUrgentVisitFlow,
    reasonType,
    hasReasonNote,
    reasonNote,
    reasonVisit,
    isHomeLocationDifferent,
    country,
    state,
  ]);

  const handleSelectReasonType = ({ value, visitTypeCodes }) => {
    handleFormChange({ [REASON_TYPE]: value });
    if (isRescheduleFlow) {
      const targetVisitType =
        allVisitTypes.find(({ code }) => code === visitTypeCodes[0]) || {};
      setVisitType(targetVisitType);
    }
  };

  const handleNext = e => {
    e.preventDefault();

    const targetReason = reasonTypesList.find(
      ({ value }) => value === formState[REASON_TYPE],
    );

    setReason({
      ...formState,
      reasonType: {
        id: targetReason.value,
        name: targetReason.label,
        visitTypeCodes: targetReason.visitTypeCodes,
      },
    });

    if (isRescheduleFlow) {
      const locationId = get(rescheduleData, 'location.id');
      const providerId = get(rescheduleData, 'providerInfo.id');
      redirectToRescheduleProviderDetails({ locationId, providerId });
    } else {
      dispatch(appointmentProgressActions.determineNextStepAfterVisitDetails());
    }
  };

  useEffect(() => {
    if (isUrgentVisitFlow) {
      fetchReasonVisit();
    }
  }, []);

  useEffect(() => {
    if (isOnlyOneReasonTypeInList) {
      handleFormChange({ [REASON_TYPE]: reasonTypesList[0].value });
    }
  }, [reasonTypesList, isOnlyOneReasonTypeInList]);

  useEffect(() => {
    if (!isInlineVisitType && !hasReasonNote) {
      handleFormChange({ [REASON_NOTE]: '' });
    }
  }, [hasReasonNote, isInlineVisitType]);

  useEffect(() => {
    handleFormChange({ [COUNTRY]: initCountryFormValue });
  }, [initCountryFormValue]);

  useEffect(() => {
    if (stateList.length && careUserStateName) {
      const userState = stateList.find(s => s.label === careUserStateName);

      if (userState) handleFormChange({ [STATE]: userState.label });
    }
  }, [stateList, careUserStateName]);

  return (
    <div styleName="root">
      <div>{loading && <Loader styleName="loader" />}</div>
      <MetaHelmet title="pages.title.getCare.details" />
      <AppointmentHeader
        title="appointments.form.visitDetails"
        handleBack={navigateToPrevPage}
      />
      <div>
        <form onSubmit={handleNext}>
          {!isOnlyOneReasonTypeInList && (
            <SelectCustom
              {...makeField(REASON_TYPE)}
              value={reasonType}
              options={reasonTypesList}
              onChange={handleSelectReasonType}
            />
          )}

          {isUrgentVisitFlow && (
            <SelectCustom
              {...makeField(REASON_VISIT)}
              multi
              value={reasonVisit}
              options={reasonVisitListOptions}
            />
          )}

          {isUrgentVisitFlow ? (
            <TextInput
              {...makeField(REASON_NOTE)}
              value={reasonNote}
              disabled={isUrgentVisitFlow && !hasReasonNote}
            />
          ) : (
            <TextInput
              {...makeField(REASON_NOTE)}
              value={reasonNote}
              label={formatMessage({ id: 'fields.reasonNote' })}
              placeholder={formatMessage({
                id: 'fields.reasonNote.placeholder',
              })}
            />
          )}

          {showHomeLocationCheckbox && (
            <Checkbox
              {...makeField(IS_HOME_LOCATION_DIFFERENT)}
              checked={Boolean(isHomeLocationDifferent)}
            />
          )}

          {Boolean(isHomeLocationDifferent) && (
            <div>
              <SelectCustom
                {...makeField(
                  COUNTRY,
                  Boolean(isHomeLocationDifferent) &&
                    FIELD_FILLING_TYPES.REQUIRED,
                )}
                value={country}
                onChange={value => handleFormChange({ [COUNTRY]: value.value })}
                options={countries}
                needUpdateDefaultValue
                required={Boolean(isHomeLocationDifferent)}
                disabled
              />

              <SelectCustom
                {...makeField(
                  STATE,
                  Boolean(isHomeLocationDifferent) &&
                    FIELD_FILLING_TYPES.REQUIRED,
                )}
                onChange={value => handleFormChange({ [STATE]: value.value })}
                deleteRemoves={false}
                backspaceRemoves={false}
                value={state}
                options={stateOptions}
                needUpdateDefaultValue
                required={Boolean(isHomeLocationDifferent)}
              />
            </div>
          )}

          <div className="mt4 tr">
            <Button
              id={SUBMIT_REASON_TYPE}
              raised
              color="primary"
              disabled={!isValid}
            >
              {formatMessage({ id: 'common.next' })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

AppointmentVisitDetails.propTypes = {
  services: PropTypes.array,
  formatMessage: PropTypes.func,
  updateData: PropTypes.func,
  setReason: PropTypes.func,
  loading: PropTypes.bool,
  reasonTypesList: PropTypes.array,
  reasonVisitList: PropTypes.array,
  savedReason: PropTypes.object,
  organization: PropTypes.object,
  requiredSteps: PropTypes.array,
  isUrgentVisitFlow: PropTypes.bool,
  fetchReasonVisit: PropTypes.func,
  visitTypeCode: PropTypes.string,
  isEnabledInsurance: PropTypes.bool,
  setVisitType: PropTypes.func,
  clearRescheduleData: PropTypes.func,
  isRescheduleFlow: PropTypes.bool,
  allVisitTypes: PropTypes.array,
  rescheduleData: PropTypes.object,
  careUserSkipInsurance: PropTypes.bool,
  selectedOrganizationTenantSkipInsurance: PropTypes.bool,
  countries: PropTypes.array.isRequired,
};

export default enhance(AppointmentVisitDetails);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentVisitDetails/index.jsx